/** Clips on the Watch page: suggestions (Accept posts the clip at once; Tweak / Reject), each with why it was
 * suggested and its shot statistics; "Suggest from here" (the playhead); "Create clip from In/Out" (Enter) posts a
 * hand-made clip at once; drafts with ±frame/±beat editors and batch "Create clips" (422 lists every reason); the
 * video's clips with their cut status. Length checks use the server's clip settings (`Timeline.clip_limits`). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ifMatch, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { StatusPill } from "../../components/StatusPill";
import { fmtMs } from "../../lib/frames";
import { useMe } from "../../lib/me";
import { clipProblems, clipWarnings, type ClipLimits } from "../clocks";
import { errorText, WriteError } from "../common";
import { KeyButton, useKeyBindings, type KeyBinding } from "../keys";
import type { FrameController } from "../player/controller";
import type { InOut } from "../player/Player";
import { shotStats, shotStatsText } from "../timeline/project";
import type { ClipPatch, MvClipOut, Suggestion, Timeline } from "../types";
import { timelineKey, useClipSubitems } from "./data";
import {
  cutStatus, fromSuggestion, inOutOrigin, loadDrafts, manualDraft, pendingSuggestions, saveDrafts, stepEdge, toClipIn, winKey,
  type Draft, type Win,
} from "./drafts";

/** Plain-language labels of the server's suggestion reasons (`vp.dsp.suggest`). */
const REASON_LABELS: Record<string, string> = {
  tiktok_hot: "hot TikTok window", chorus: "chorus", our_artist_lines: "our artist's lines", cut_in: "starts on a cut",
  cut_out: "ends on a cut", section_start: "section start", line_start: "line start", downbeat: "on a downbeat",
  section: "section", fill: "bar-aligned fill",
};

export function reasonLabel(r: string): string {
  const m = /^cuts ([\d.]+)\/s$/.exec(r);
  if (m) return `${m[1]} cuts/s`;
  return REASON_LABELS[r] ?? r;
}

function Reasons({ reasons }: { reasons: string[] }) {
  if (!reasons.length) return null;
  return (
    <span className="ws8-reasons" aria-label={`Why: ${reasons.map(reasonLabel).join(", ")}`}>
      {reasons.map((r) => <span key={r} className="ws8-reason">{reasonLabel(r)}</span>)}
    </span>
  );
}

function WindowEditor({ id, name, win, onChange, fps, beats, ctrl, inOut }: {
  id: string; name: string; win: Win; onChange: (w: Win) => void; fps: number; beats: readonly number[];
  ctrl: FrameController; inOut?: InOut;
}) {
  const edge = (e: "in" | "out") => {
    const label = e === "in" ? "In" : "Out";
    const v = e === "in" ? win.inMs : win.outMs;
    const step = (how: "frame" | "beat", dir: 1 | -1) => onChange(stepEdge(win, e, how, dir, fps, beats));
    return (
      <div className="ws8-row" role="group" aria-label={`${name} ${label}`}>
        <button type="button" data-testid={`${id}-${e}-minus-beat`} aria-label={`${label} −1 beat`}
          disabled={beats.length === 0} onClick={() => step("beat", -1)}>−beat</button>
        <button type="button" data-testid={`${id}-${e}-minus-frame`} aria-label={`${label} −1 frame`}
          onClick={() => step("frame", -1)}>−frame</button>
        <label className="ws8-inline">
          {label} (ms){" "}
          <input type="number" min={0} step={1} data-testid={`${id}-${e}-ms`} value={v}
            onChange={(ev) => {
              const n = Math.round(Number(ev.target.value));
              if (Number.isFinite(n) && n >= 0) onChange(e === "in" ? { ...win, inMs: n } : { ...win, outMs: n });
            }} />
        </label>
        <button type="button" data-testid={`${id}-${e}-plus-frame`} aria-label={`${label} +1 frame`}
          onClick={() => step("frame", 1)}>+frame</button>
        <button type="button" data-testid={`${id}-${e}-plus-beat`} aria-label={`${label} +1 beat`}
          disabled={beats.length === 0} onClick={() => step("beat", 1)}>+beat</button>
        <button type="button" data-testid={`${id}-${e}-seek`} aria-label={`Seek to ${label}`}
          onClick={() => ctrl.seekMs(v)}>Seek {fmtMs(v)}</button>
        {inOut && (
          <button type="button" data-testid={`${id}-${e}-from-player`} aria-label={`${label} from the playhead`}
            onClick={() => onChange(e === "in" ? { ...win, inMs: ctrl.getState().tMs } : { ...win, outMs: ctrl.getState().tMs })}>
            From playhead
          </button>
        )}
      </div>
    );
  };
  return <div className="ws8-editor">{edge("in")}{edge("out")}</div>;
}

function Problems({ problems, warnings = [] }: { problems: string[]; warnings?: string[] }) {
  if (!problems.length && !warnings.length) return null;
  return (
    <ul className="ws8-warn" aria-label="Checks before sending">
      {problems.map((p) => <li key={p}>{p}</li>)}
      {warnings.map((p) => <li key={p} className="ws8-muted">{p}</li>)}
    </ul>
  );
}

export function ClipsPanel({ tl, ctrl, fps, inOut, keys = true }: {
  tl: Timeline; ctrl: FrameController; fps: number; inOut: InOut; keys?: boolean;
}) {
  const videoId = tl.video_id;
  const qc = useQueryClient();
  const announce = useAnnounce();
  const sub = useClipSubitems(videoId);
  const me = useMe();
  const [drafts, setDrafts] = useState<Draft[]>(() => loadDrafts(videoId));
  const [rejected, setRejected] = useState<Set<string>>(() => new Set());
  const [tweak, setTweak] = useState<{ id: number; win: Win } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [fromHere, setFromHere] = useState<{ at: number; items: Suggestion[] } | null>(null);
  useEffect(() => saveDrafts(videoId, drafts), [videoId, drafts]);
  const beats = tl.beats_mv_ms;
  const segs = tl.segments;
  const limits: ClipLimits | null = tl.clip_limits ?? null;
  const tlFrom = useMemo(() => (fromHere ? { ...tl, suggestions: [...fromHere.items, ...tl.suggestions] } : tl),
    [tl, fromHere]);
  const pending = useMemo(() => pendingSuggestions(tlFrom, drafts, rejected), [tlFrom, drafts, rejected]);
  const existing = tl.clips.filter((c) => c.state === "accepted");
  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: timelineKey(videoId) });
    void qc.invalidateQueries({ queryKey: qk.subitems("videos", videoId) });
  };

  const patch = useMutation({
    mutationFn: ({ clip, body }: { clip: MvClipOut; body: ClipPatch; what: string }) =>
      unwrap(api.PATCH("/api/clips/{clip_id}", { params: { path: { clip_id: clip.id }, header: ifMatch(clip.version) },
        body })),
    onSuccess: (c, v) => {
      setError(null);
      setTweak(null);
      announce(`${v.what} clip #${c.id}`);
      refresh();
    },
    onError: (e, v) => {
      setError(e);
      announce(`${v.what} clip #${v.clip.id} failed: ${errorText(e)}`, "error");
    },
  });

  const create = useMutation({
    mutationFn: (ds: Draft[]) => unwrap(api.POST("/api/videos/{video_id}/clips",
      { params: { path: { video_id: videoId } }, body: { clips: ds.map(toClipIn) } })),
    onSuccess: (clips) => {
      setError(null);
      setDrafts([]);
      announce(`Created ${clips.length} clip${clips.length === 1 ? "" : "s"}: ${clips.map((c) => `#${c.id}`).join(", ")}`);
      refresh();
    },
    onError: (e) => {
      setError(e);
      announce(`Create clips failed: ${errorText(e)}`, "error");
    },
  });

  /** One clip posted right away (Accept on a suggestion, Enter on In/Out): no draft, no confirm step. */
  const createOne = useMutation({
    mutationFn: ({ d }: { d: Draft; what: string; clearInOut?: boolean }) => unwrap(api.POST("/api/videos/{video_id}/clips",
      { params: { path: { video_id: videoId } }, body: { clips: [toClipIn(d)] } })),
    onSuccess: (clips, v) => {
      setError(null);
      const c = clips[0];
      announce(`${v.what}${c ? ` as clip #${c.id}` : ""}${c?.warnings?.length ? ` (${c.warnings.join("; ")})` : ""}`);
      if (v.clearInOut) inOut.set({ in: null, out: null, loop: null });
      refresh();
    },
    onError: (e, v) => {
      setError(e);
      announce(`${v.what} failed: ${errorText(e)}`, "error");
    },
  });

  const suggestFrom = useMutation({
    mutationFn: (fromMs: number) => unwrap(api.POST("/api/videos/{video_id}/suggest",
      { params: { path: { video_id: videoId }, query: { from_ms: fromMs, n: 5 } } })),
    onSuccess: (items, fromMs) => {
      setError(null);
      setFromHere({ at: fromMs, items });
      announce(items.length ? `${items.length} suggestion${items.length === 1 ? "" : "s"} from ${fmtMs(fromMs)}`
        : `No clip fits from ${fmtMs(fromMs)} (sung lines, length, MV edits)`);
    },
    onError: (e) => {
      setError(e);
      announce(`Suggest from here failed: ${errorText(e)}`, "error");
    },
  });

  // clips made from In/Out carry who made them: an agent token's clips are `agent` (normal length range), not `manual`
  const ioOrigin = inOutOrigin(me.data?.kind);
  const addDraft = (d: Draft) => setDrafts((xs) => (xs.some((x) => x.key === d.key) ? xs : [...xs, d]));
  const updateDraft = (key: string, p: Partial<Draft>) => setDrafts((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const canAddFromInOut = inOut.inMs != null && inOut.outMs != null && inOut.outMs > inOut.inMs;
  const ioProblems = canAddFromInOut ? clipProblems(inOut.inMs, inOut.outMs, segs, { limits, origin: ioOrigin }) : [];
  const ioWarnings = canAddFromInOut ? clipWarnings(inOut.inMs, inOut.outMs, { limits, origin: ioOrigin }) : [];
  const createKey: KeyBinding = { key: "Enter", label: "Create clip from In/Out", testId: "create-from-inout",
    disabled: !canAddFromInOut || createOne.isPending, run: () => {
      if (inOut.inMs == null || inOut.outMs == null || inOut.outMs <= inOut.inMs) return;
      const d = { ...manualDraft(inOut.inMs, inOut.outMs, 0, ioOrigin), label: `Clip ${fmtMs(inOut.inMs)}` };
      createOne.mutate({ d, what: `Created ${d.label} ${fmtMs(d.inMs)}–${fmtMs(d.outMs)}`, clearInOut: true });
    } };
  useKeyBindings([createKey], keys);

  return (
    <section className="ws8-panel" aria-labelledby="ws8-clips-h">
      <h2 id="ws8-clips-h" className="ws8-h2">Clips</h2>
      <p className="ws8-muted" data-testid="alignment-summary">
        {tl.music_start_mv_ms != null ? <>Music starts at {fmtMs(tl.music_start_mv_ms)} (MV clock). </> : <>Music start unknown. </>}
        {tl.cutscenes.length > 0 && <>Cutscenes: {tl.cutscenes.map((c) => `${c.kind} ${fmtMs(c.mv0_ms)}–${fmtMs(c.mv1_ms)}`).join(", ")}. </>}
        {segs.length > 0 && <>{segs.length} aligned segment{segs.length === 1 ? "" : "s"}; a clip must stay inside one.</>}
        {tl.music_start_mv_ms != null && (
          <> <button type="button" data-testid="seek-music-start" onClick={() => ctrl.seekMs(tl.music_start_mv_ms as number)}>
            Seek to music start</button></>
        )}
      </p>
      <WriteError error={error} onReload={refresh} />

      <h3 className="ws8-h3">Suggestions ({pending.length})</h3>
      <div className="ws8-row">
        <button type="button" data-testid="suggest-from-here" disabled={suggestFrom.isPending || segs.length === 0}
          onClick={() => suggestFrom.mutate(ctrl.getState().tMs)}>Suggest from here</button>
        {fromHere && (
          <>
            <span className="ws8-muted" data-testid="from-here-summary">
              {fromHere.items.length} from {fmtMs(fromHere.at)}
            </span>
            <button type="button" data-testid="clear-from-here" onClick={() => setFromHere(null)}>Clear</button>
          </>
        )}
        <span className="ws8-muted">Shift-click a section, line, cut or hot window to set In; Alt-click to set Out.</span>
      </div>
      {pending.length === 0 ? <p className="ws8-muted" data-testid="no-suggestions">No open suggestions.</p> : (
        <ul className="ws8-list" data-testid="suggestions">
          {pending.map((p, i) => {
            const isClip = p.kind === "clip";
            const t0 = isClip ? p.clip.t_mv_in_ms : p.s.t_mv_in_ms;
            const t1 = isClip ? p.clip.t_mv_out_ms : p.s.t_mv_out_ms;
            const label = isClip ? p.clip.label ?? `clip ${p.clip.id}` : p.s.label;
            const score = isClip ? p.clip.score : p.s.score;
            const reasons = isClip ? (p.clip.reason ?? "").split(/;\s*|,\s*/).filter(Boolean) : p.s.reasons;
            const name = `Suggestion ${i + 1}: ${label}`;
            const tweaking = isClip && tweak?.id === p.clip.id;
            const shots = (tl.scenes ?? []).length ? shotStatsText(shotStats(tl, t0, t1)) : null;
            return (
              <li key={p.key} data-testid={`suggestion-${i}`}><div role="group" aria-label={name}>
                <strong>{label}</strong> {fmtMs(t0)}–{fmtMs(t1)} ({fmtMs(t1 - t0)})
                {score != null && <span className="ws8-muted"> · score {score.toFixed(2)}</span>}
                <Reasons reasons={reasons} />
                {isClip && <span className="ws8-muted"> · proposed clip #{p.clip.id}</span>}
                {shots && <div className="ws8-muted" data-testid={`suggestion-${i}-shots`}>Shots: {shots}</div>}
                <div className="ws8-row">
                  <button type="button" data-testid={`suggestion-${i}-seek`} onClick={() => ctrl.seekMs(t0)}>Seek</button>
                  <button type="button" data-testid={`suggestion-${i}-accept`} aria-label={`Accept ${label}`}
                    disabled={patch.isPending || createOne.isPending} onClick={() => {
                      if (isClip) patch.mutate({ clip: p.clip, body: { state: "accepted" }, what: "Accepted" });
                      else createOne.mutate({ d: fromSuggestion(p.s), what: `Accepted ${label}` });
                    }}>Accept</button>
                  {!isClip && (
                    <button type="button" data-testid={`suggestion-${i}-inout`} aria-label={`Use ${label} as In/Out`}
                      onClick={() => inOut.set({ in: t0, out: t1 })}>Use as In/Out</button>
                  )}
                  <button type="button" data-testid={`suggestion-${i}-tweak`} aria-label={`Tweak ${label}`}
                    onClick={() => {
                      if (isClip) setTweak({ id: p.clip.id, win: { inMs: t0, outMs: t1 } });
                      else {
                        addDraft(fromSuggestion(p.s));
                        announce(`Tweaking ${label}: edit it under New clips`);
                      }
                    }}>Tweak</button>
                  <button type="button" data-testid={`suggestion-${i}-reject`} aria-label={`Reject ${label}`}
                    disabled={patch.isPending} onClick={() => {
                      if (isClip) patch.mutate({ clip: p.clip, body: { state: "rejected" }, what: "Rejected" });
                      else {
                        setRejected((r) => new Set(r).add(winKey(t0, t1)));
                        announce(`Rejected suggestion ${label}`);
                      }
                    }}>Reject</button>
                </div>
                {tweaking && tweak && isClip && (
                  <>
                    <WindowEditor id={`tweak-${p.clip.id}`} name={label} win={tweak.win} fps={fps} beats={beats} ctrl={ctrl}
                      inOut={inOut} onChange={(w) => setTweak({ id: p.clip.id, win: w })} />
                    <Problems problems={clipProblems(tweak.win.inMs, tweak.win.outMs, segs,
                      { limits, origin: p.clip.origin === "manual" ? "manual" : "suggested" })} />
                    <div className="ws8-row">
                      <button type="button" data-testid={`tweak-${p.clip.id}-save`} disabled={patch.isPending}
                        onClick={() => patch.mutate({ clip: p.clip, what: "Tweaked and accepted",
                          body: { t_mv_in_ms: tweak.win.inMs, t_mv_out_ms: tweak.win.outMs, state: "accepted" } })}>
                        Save tweak and accept
                      </button>
                      <button type="button" data-testid={`tweak-${p.clip.id}-cancel`} onClick={() => setTweak(null)}>Cancel</button>
                    </div>
                  </>
                )}
              </div></li>
            );
          })}
        </ul>
      )}

      <h3 className="ws8-h3">New clips ({drafts.length})</h3>
      <div className="ws8-row">
        <KeyButton b={createKey} className="ws8-primary" />
        <button type="button" data-testid="add-from-inout" disabled={!canAddFromInOut} onClick={() => {
          if (!canAddFromInOut) return;
          const d = manualDraft(inOut.inMs as number, inOut.outMs as number, drafts.length + 1, ioOrigin);
          addDraft(d);
          announce(`Added draft ${d.label} ${fmtMs(d.inMs)}–${fmtMs(d.outMs)}`);
        }}>Add clip from In/Out</button>
        {!canAddFromInOut && <span className="ws8-muted">Set In and Out first (I / O, or Shift/Alt-click).</span>}
      </div>
      {canAddFromInOut && (
        <>
          {(tl.scenes ?? []).length > 0 && (
            <p className="ws8-muted" data-testid="inout-shots">
              In–Out shots: {shotStatsText(shotStats(tl, inOut.inMs as number, inOut.outMs as number))}
            </p>
          )}
          <Problems problems={ioProblems} warnings={ioWarnings} />
        </>
      )}
      {drafts.length > 0 && (
        <ul className="ws8-list" data-testid="drafts">
          {drafts.map((d, i) => (
            <li key={d.key} data-testid={`draft-${i}`}><div role="group" aria-label={`New clip ${i + 1}: ${d.label}`}>
              <div className="ws8-row">
                <label className="ws8-inline">Label <input data-testid={`draft-${i}-label`} value={d.label} maxLength={120}
                  onChange={(e) => updateDraft(d.key, { label: e.target.value })} /></label>
                <span className="ws8-muted">{fmtMs(d.outMs - d.inMs)} · {d.origin}</span>
                <button type="button" data-testid={`draft-${i}-remove`} aria-label={`Remove new clip ${d.label}`}
                  onClick={() => setDrafts((xs) => xs.filter((x) => x.key !== d.key))}>Remove</button>
              </div>
              <WindowEditor id={`draft-${i}`} name={d.label} win={d} fps={fps} beats={beats} ctrl={ctrl} inOut={inOut}
                onChange={(w) => updateDraft(d.key, w)} />
              <Problems problems={clipProblems(d.inMs, d.outMs, segs, { limits, origin: d.origin })}
                warnings={clipWarnings(d.inMs, d.outMs, { limits, origin: d.origin })} />
            </div></li>
          ))}
        </ul>
      )}
      <div className="ws8-row">
        <button type="button" className="ws8-primary" data-testid="create-clips"
          disabled={drafts.length === 0 || create.isPending} onClick={() => create.mutate(drafts)}>
          Create clips ({drafts.length})
        </button>
        {drafts.length > 0 && (
          <button type="button" data-testid="clear-drafts" onClick={() => setDrafts([])}>Discard new clips</button>
        )}
      </div>

      <h3 className="ws8-h3">Clips on this video ({existing.length})</h3>
      {existing.length === 0 ? <p className="ws8-muted" data-testid="no-clips">No accepted clips yet.</p> : (
        <table className="ws8-table" data-testid="clips-table">
          <thead>
            <tr><th scope="col">Clip</th><th scope="col">MV in–out</th><th scope="col">Length</th><th scope="col">Template</th>
              <th scope="col">Cut</th><th scope="col">Actions</th></tr>
          </thead>
          <tbody>
            {existing.map((c) => {
              const cut = cutStatus(c, sub.data);
              return (
                <tr key={c.id} data-testid={`clip-row-${c.id}`}>
                  <td><Link to={`/clips/${c.id}`}>#{c.id} {c.label ?? ""}</Link></td>
                  <td>{fmtMs(c.t_mv_in_ms)}–{fmtMs(c.t_mv_out_ms)}</td>
                  <td>{fmtMs(c.dur_ms)}</td>
                  <td>{c.template_state.replace("_", " ")}{c.render_count ? ` · ${c.render_count} renders` : ""}</td>
                  <td><StatusPill state={cut.state} label="cut" /></td>
                  <td className="ws8-row">
                    <button type="button" data-testid={`clip-${c.id}-seek`} aria-label={`Seek to clip #${c.id}`}
                      onClick={() => ctrl.seekMs(c.t_mv_in_ms)}>Seek</button>
                    <button type="button" data-testid={`clip-${c.id}-inout`} aria-label={`Set In/Out to clip #${c.id}`}
                      onClick={() => inOut.set({ in: c.t_mv_in_ms, out: c.t_mv_out_ms })}>Use as In/Out</button>
                    <Link className="button" to={`/choose?clip=${c.id}`} data-testid={`clip-${c.id}-choose`}
                      aria-label={`Choose templates for clip #${c.id}`}>Choose templates</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
